import React, { useState, useRef, useEffect } from 'react';
import '../ut_styles/imagecomp.scss';
import SvgIcn from "../data/IconCompo";
import { updateFavicon } from '../utils/favicon';
import BackButton from '../components/BackButton';

const CompareImage = () => {
  useEffect(() => {
    updateFavicon('compare');
  }, []);

  const [sliderPosition, setSliderPosition] = useState(50);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [opacity, setOpacity] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [singleImageWarning, setSingleImageWarning] = useState(false);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  const updateSliderPosition = (clientX) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newPosition = ((clientX - rect.left) / rect.width) * 100;
      setSliderPosition(Math.min(Math.max(newPosition, 0), 100));
    }
  };

  const handleMouseMove = (e) => updateSliderPosition(e.clientX);
  const handleTouchMove = (e) => {
    e.preventDefault();
    updateSliderPosition(e.touches[0].clientX);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (beforeImage && afterImage && container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('touchmove', handleTouchMove);
    }
    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [beforeImage, afterImage]);

  const handleImageUpload = (files) => {
    const [first, second] = files.slice(0, 2);
    if (files.length === 1) {
      setSingleImageWarning(true);
      setTimeout(() => setSingleImageWarning(false), 6000);
    }

    if (first) setBeforeImage(URL.createObjectURL(first));
    if (second) setAfterImage(URL.createObjectURL(second));
  };

  const handleFileChange = (e) => {
    handleImageUpload(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleImageUpload(Array.from(e.dataTransfer.files));
    setIsDragging(false);
  };

  const handleClearImages = () => {
    setBeforeImage(null);
    setAfterImage(null);
    setOpacity(1);
    setSliderPosition(50);
  };

  return (
    <div className="compare-image-container">
      <BackButton />
      <SvgIcn name="compareimage_logo" className="logo"/>
      <h1>Image Comparison Slider</h1>
      <div className="controls-container">
        <div className="upload-buttons">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          {beforeImage && afterImage ? (
            <button onClick={handleClearImages} className="clear-btn">
             Clear <SvgIcn name="clear" hover="icon08" /> 
            </button>
          ) : (
            <button onClick={() => fileInputRef.current.click()} className="upload-btn">
              Upload <SvgIcn name="images" hover="icon08" /> 
            </button>
          )}
        </div>
        <div className="opacity-control">
          <input
            type="range"
            min="0"
            max="100"
            value={opacity * 100}
            onChange={(e) => setOpacity(e.target.value / 100)}
            className={`opacity-slider ${!beforeImage || !afterImage ? 'disabled' : ''}`}
            disabled={!beforeImage || !afterImage}
          />
          <span className="opacity-value">{Math.round(opacity * 100)}%</span>
        </div>
      </div>

      <div 
        className={`image-compare ${isDragging ? 'dragging' : ''}`} 
        ref={containerRef}
        onDragEnter={() => setIsDragging(true)}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {beforeImage && afterImage ? (
          <>
            <img className="image-before" src={beforeImage} alt="Before" />
            <img
              className="image-after"
              src={afterImage}
              alt="After"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`, opacity }}
            />
            <div className="slider-line" style={{ left: `${sliderPosition}%` }}>
              <div className="slider-icon">
                <SvgIcn name="start" />
              </div>
            </div>
          </>
        ) : (
          <div className="image-placeholder">
            <SvgIcn name="image" />
            <h3 className="upload-instruction">
              {singleImageWarning 
                ? "Please upload 2 images for comparison" 
                : "Drag & drop 2 images here or use the upload button"}
            </h3>
          </div>
        )}
      </div>
      <h3>{beforeImage && afterImage ? "Drag the slider to compare images or upload new ones" : "Upload two images to compare"}</h3>
    </div>
  );
};

export default CompareImage;
